/**
 * Duquezzi - Infinite Scroll & Load More Web Component
 * Seamlessly integrates with Prestige 10.10.1 architecture
 */

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.getAttribute('section-id') || '';
    this.targetListId = this.getAttribute('target-list') || '';
    this.currentPage = parseInt(this.getAttribute('current-page') || '1', 10);
    this.totalPages = parseInt(this.getAttribute('total-pages') || '1', 10);
    this.nextUrl = this.getAttribute('next-url') || null;
    this.autoLoadLimit = parseInt(this.getAttribute('auto-load-limit') || '2', 10);
    this.mode = this.getAttribute('mode') || 'scroll';

    this.autoLoadCount = 0;
    this.isLoading = false;
    this.abortController = null;
    this.observer = null;

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    this.onProductCardClick = this.onProductCardClick.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
  }

  connectedCallback() {
    this.targetList = document.getElementById(this.targetListId) || document.querySelector('.product-list');
    this.sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.loader = this.querySelector('.infinite-scroll__loader');
    this.button = this.querySelector('.infinite-scroll__button');
    this.endMessage = this.querySelector('.infinite-scroll__end-message');

    // Attach click listener to Load More button
    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick);
    }

    // Attach click listener on target product cards to preserve state when navigating to PDP
    if (this.targetList) {
      this.targetList.addEventListener('click', this.onProductCardClick);
    }

    window.addEventListener('pageshow', this.onPageShow);

    // Check and restore scroll state if returning from PDP via back button
    this.restoreStateIfNeeded().finally(() => {
      if (this.isConnected) this.initObserver();
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClick);
    }

    if (this.targetList) {
      this.targetList.removeEventListener('click', this.onProductCardClick);
    }

    window.removeEventListener('pageshow', this.onPageShow);
  }

  initObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.mode !== 'scroll' || !this.sentinel || !this.nextUrl) {
      return;
    }

    if (this.autoLoadCount >= this.autoLoadLimit) {
      if (this.button && this.nextUrl) {
        this.button.hidden = false;
      }
      return;
    }

    this.observer = new IntersectionObserver(this.onIntersection, {
      root: null,
      rootMargin: '600px 0px 400px 0px',
      threshold: 0
    });

    this.observer.observe(this.sentinel);
  }

  onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
        this.loadNextPage();
      }
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.isLoading && this.nextUrl) {
      this.loadNextPage();
    }
  }

  onProductCardClick(event) {
    const card = event.target.closest('.product-card');
    if (!card) return;

    try {
      const collectionUrl = new URL(window.location.href);
      collectionUrl.searchParams.delete('page');
      const collectionPath = collectionUrl.pathname + collectionUrl.search;
      const state = {
        path: collectionPath,
        page: this.currentPage,
        scrollY: window.scrollY
      };
      sessionStorage.setItem('dq_infinite_scroll_state', JSON.stringify(state));

      // Return to the base collection URL so a full page navigation back starts with page 1.
      if (collectionUrl.href !== window.location.href) {
        window.history.replaceState(window.history.state, '', collectionUrl.toString());
      }
    } catch (e) {
      // Ignore sessionStorage quota or access errors
    }
  }

  onPageShow(event) {
    if (!event.persisted) return;

    try {
      const raw = sessionStorage.getItem('dq_infinite_scroll_state');
      if (!raw) return;

      const state = JSON.parse(raw);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('page');

      if (state && state.path === currentUrl.pathname + currentUrl.search) {
        sessionStorage.removeItem('dq_infinite_scroll_state');
        window.scrollTo({ top: state.scrollY, behavior: 'instant' });
      }
    } catch (e) {
      sessionStorage.removeItem('dq_infinite_scroll_state');
    }
  }

  async restoreStateIfNeeded() {
    try {
      const raw = sessionStorage.getItem('dq_infinite_scroll_state');
      if (!raw) return;

      const state = JSON.parse(raw);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('page');
      const currentPath = currentUrl.pathname + currentUrl.search;
      const savedUrl = new URL(state.path, window.location.origin);
      savedUrl.searchParams.delete('page');
      const savedPath = savedUrl.pathname + savedUrl.search;

      if (state && savedPath === currentPath && state.page > 1) {
        sessionStorage.removeItem('dq_infinite_scroll_state');

        // Sequentially load batches up to the saved page
        while (this.currentPage < state.page && this.nextUrl && !this.isLoading) {
          await this.loadNextPage({ track: false });
        }

        // Restore scroll position after DOM settles
        requestAnimationFrame(() => {
          window.scrollTo({
            top: state.scrollY,
            behavior: 'instant'
          });
        });
      } else {
        sessionStorage.removeItem('dq_infinite_scroll_state');
      }
    } catch (e) {
      sessionStorage.removeItem('dq_infinite_scroll_state');
    }
  }

  async loadNextPage(options = {}) {
    if (this.isLoading || !this.nextUrl) {
      return;
    }

    this.isLoading = true;

    // UI: Show loader, hide button
    if (this.loader) {
      this.loader.hidden = false;
      this.loader.setAttribute('aria-busy', 'true');
    }
    if (this.button) {
      this.button.hidden = true;
    }

    // Trigger theme global top loading bar
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      this.abortController = new AbortController();

      const fetchUrl = new URL(this.nextUrl, window.location.origin);
      if (this.sectionId) {
        fetchUrl.searchParams.set('section_id', this.sectionId);
      }

      const response = await fetch(fetchUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Locate target product list in response
      const targetSelector = this.targetListId ? `#${this.targetListId}` : '.product-list';
      const newProductList = doc.querySelector(targetSelector);

      if (newProductList && this.targetList) {
        const newCards = Array.from(newProductList.querySelectorAll('.product-card'));
        const existingHandles = new Set(
          Array.from(this.targetList.querySelectorAll('.product-card')).map((card) => card.getAttribute('handle'))
        );

        const cardsToAppend = newCards.filter((card) => {
          const handle = card.getAttribute('handle');
          return handle ? !existingHandles.has(handle) : true;
        });

        // Ensure newly appended cards display immediately without reveal delay lag
        cardsToAppend.forEach((card) => {
          card.removeAttribute('reveal-on-scroll');
          this.targetList.appendChild(card);
        });

        // Dispatch GA4 view_item_list only for newly browsed batches, not state restoration.
        if (options.track !== false) {
          this.trackGA4ViewItemList(cardsToAppend);
        }
      }

      // Find next pagination URL from response
      const newInfiniteScroll = doc.querySelector('infinite-scroll');
      const newNextUrl = newInfiniteScroll ? newInfiniteScroll.getAttribute('next-url') : null;

      this.currentPage += 1;
      this.autoLoadCount += 1;

      if (newNextUrl) {
        this.nextUrl = newNextUrl;
        this.setAttribute('next-url', newNextUrl);

        // Check if auto load threshold reached
        if (this.mode === 'scroll' && this.autoLoadCount >= this.autoLoadLimit) {
          if (this.observer) {
            this.observer.disconnect();
          }
          if (this.button) {
            this.button.hidden = false;
          }
        }
      } else {
        // No further pages
        this.nextUrl = null;
        this.removeAttribute('next-url');

        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.button) {
          this.button.hidden = true;
        }
        if (this.sentinel) {
          this.sentinel.hidden = true;
        }
        if (this.endMessage) {
          this.endMessage.hidden = false;
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[InfiniteScroll] Error loading page:', error);
        // On error, show button so user can retry
        if (this.button && this.nextUrl) {
          this.button.hidden = false;
        }
      }
    } finally {
      this.isLoading = false;

      if (this.loader) {
        this.loader.hidden = true;
        this.loader.setAttribute('aria-busy', 'false');
      }

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  trackGA4ViewItemList(cards) {
    if (!cards || cards.length === 0) return;

    try {
      const totalBefore = this.targetList.querySelectorAll('.product-card').length - cards.length;
      const items = cards.map((card, idx) => {
        const titleEl = card.querySelector('.product-title');
        const vendorEl = card.querySelector('.vendor, .smallcaps');
        const priceEl = card.querySelector('.price-list');
        const priceText = priceEl ? priceEl.textContent : '';
        const priceMatch = priceText.match(/[\d]+(?:[.,]\d+)?/);
        const price = priceMatch ? parseFloat(priceMatch[0].replace(',', '.')) : 0;

        return {
          item_id: card.getAttribute('handle') || '',
          item_name: titleEl ? titleEl.textContent.trim() : '',
          item_brand: vendorEl ? vendorEl.textContent.trim() : 'Duquezzi',
          price: price,
          index: totalBefore + idx + 1
        };
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: this.sectionId,
          item_list_name: document.title || 'Collection',
          items: items
        }
      });
    } catch (e) {
      // Analytics error safety
    }
  }
}

if (!window.customElements.get('infinite-scroll')) {
  window.customElements.define('infinite-scroll', InfiniteScroll);
}
